import _ from '@lodash';
import { useEffect, useState } from 'react';
import { Tooltip, IconButton, TableCell, Icon, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@material-ui/core';
import FuseLoading from '@fuse/core/FuseLoading';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { showMessage } from 'app/store/fuse/messageSlice';
import { PiSealWarningDuotone } from 'react-icons/pi';

import GradualOutputLevelCorrespondence from '../enum/GradualOutputLevelCorrespondence';
import ReasoningLevel from '../enum/ReasoningLevel';
import IncorrectAnswersBelievable from '../enum/IncorrectAnswersBelievable';
import ShouldBeDeleted from '../enum/ShouldBeDeleted';

import EditFormHtmlField from 'modules/ui/editform/fields/EditFormHtmlField';
import EditFormSelectField from 'modules/ui/editform/fields/EditFormSelectField';
import FmStorageTableService from 'app/admin/fileManager/service/FmStorageTableService';
import FmBrowserTableService from 'app/admin/fileManager/service/FmBrowserTableService';
import nBossConnection from 'modules/base/service/nBossConnection';

import ReviewEditService from 'app/admin/review/service/ReviewEditService';
import ReviewEditDto from 'app/admin/review/dto/ReviewEditDto';

function TableQuestionViewButton(props) {
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [review, setReview] = useState(false);
	const [questionText, setQuestionText] = useState(null);

	const dispatch = useDispatch();
	const user = useSelector(({ auth }) => auth.user);

	const schema = yup.object().shape({});

	const methods = useForm({
		defaultValues: { ...(props.defaultValues || {}), comments: '' },
		mode: 'onChange',
		resolver: yupResolver(schema)
	});

	useEffect(() => {
		if (open) {
			setLoading(true);
			props.button.service.showQuestion(props.item.id).then(resp => {
				setQuestionText(trimEdgeBr(resp));
				setLoading(false);
			});
		}
	}, [open]);

	const handleSave = value => {
		value.felhasznalo = user.data.id;
		value.kerdes = props.item.id;

		const successMessage = 'A vélemény beküldésre került.';
		const errorMessage = 'Hiba történt vélemény küldése során, kérjük, próbálja kis elteltével múlva újra.';

		ReviewEditService.submitReview(new ReviewEditDto().setup(value)).then(resp => {
			if (resp.successful) {
				dispatch(showMessage({ message: successMessage }));
				setOpen(false);
			} else {
				dispatch(showMessage({ message: errorMessage }));
			}
		}).catch(() => {
			dispatch(showMessage({ message: errorMessage }));
		});
	};

	const trimEdgeBr = html => {
		if (!html) return html;
		return html.replace(/^(?:\s*<br\s*\/?>\s*)+/i, '').replace(/(?:\s*<br\s*\/?>\s*)+(?=\s*<\/div>\s*$)/i, '').replace(/(?:\s*<br\s*\/?>\s*)+$/i, '');
	};

	return (
		<TableCell className='w-auto text-center' padding='none'>

			<Tooltip title='Kérdés megtekintése'>
				<IconButton onClick={() => setOpen(true)}>
					<Icon fontSize='small'>visibility</Icon>
				</IconButton>
			</Tooltip>

			<Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth={false} PaperProps={{ style: { maxWidth: 640 } }}>

				<DialogTitle disableTypography id='form-dialog-title' className='flex items-center justify-between border-b'>
					{review ? 'Kérdés véleményezése' : 'Kérdés megtekintése'}
					<Button onClick={() => setOpen(false)} color='primary' variant='contained'>Bezár</Button>
				</DialogTitle>

				<DialogContent>

					{loading && <div className='pb-28'><FuseLoading /></div>}

					{(!loading && questionText !== null /*&& !review*/) && <div className='py-16' dangerouslySetInnerHTML={{ __html: questionText }} />}

					{(!loading && questionText !== null && review) && <div>

						<FormProvider {...methods}>
							<Controller control={methods.control} name='gradualOutputLevelCorrespondence' render={({ field, fieldState }) => (<EditFormSelectField
								field={field}
								fieldState={fieldState}
								config={{ topic: 'common' }}
								fieldConfig={{
									key: 'gradualOutputLevelCorrespondence',
									text: 'A graduális kimeneti szintnek megfelelő a kérdés?',
									type: 'enum',
									items: Object.keys(GradualOutputLevelCorrespondence).map(key => { return { label: GradualOutputLevelCorrespondence[key], value: key }; })
								}} />
							)} />
							<Controller control={methods.control} name='reasoningLevel' render={({ field, fieldState }) => (<EditFormSelectField
								field={field}
								fieldState={fieldState}
								config={{ topic: 'common' }}
								fieldConfig={{
									key: 'reasoningLevel',
									text: 'Milyen gondolkodást mér a kérdés?',
									type: 'enum',
									items: Object.keys(ReasoningLevel).map(key => { return { label: ReasoningLevel[key], value: key }; })
								}} />
							)} />
							<Controller control={methods.control} name='clearQuestion' render={({ field, fieldState }) => (<EditFormSelectField
								field={field}
								fieldState={fieldState}
								config={{ topic: 'common' }}
								fieldConfig={{
									key: 'clearQuestion',
									text: 'A kérdés világos, egyértelmű?',
									type: 'enum',
									items: [
										{ label: 'Igen', value: true },
										{ label: 'Nem', value: false }
									]
								}} />
							)} />
							<Controller control={methods.control} name='incorrectAnswersBelievable' render={({ field, fieldState }) => (<EditFormSelectField
								field={field}
								fieldState={fieldState}
								config={{ topic: 'common' }}
								fieldConfig={{
									key: 'incorrectAnswersBelievable',
									text: 'A helytelen válaszlehetőségek milyen minőségűek?',
									type: 'enum',
									items: Object.keys(IncorrectAnswersBelievable).map(key => { return { label: IncorrectAnswersBelievable[key], value: key }; })
								}} />
							)} />
							<Controller control={methods.control} name='grammarProblems' render={({ field, fieldState }) => (<EditFormSelectField
								field={field}
								fieldState={fieldState}
								config={{ topic: 'common' }}
								fieldConfig={{
									key: 'grammarProblems',
									text: 'Nyelvtani hiba, vagy nyelvhelyességi hiba van-e?',
									type: 'enum',
									items: [
										{ label: 'Van', value: true },
										{ label: 'Nincs', value: false }
									]
								}} />
							)} />
							<Controller control={methods.control} name='shouldBeDeleted' render={({ field, fieldState }) => (<EditFormSelectField
								field={field}
								fieldState={fieldState}
								config={{ topic: 'common' }}
								fieldConfig={{
									key: 'shouldBeDeleted',
									text: 'Törölni kell-e a kérdést?',
									type: 'enum',
									items: Object.keys(ShouldBeDeleted).map(key => { return { label: ShouldBeDeleted[key], value: key }; })
								}} />
							)} />
							<div className='flex items-center text-sm gap-12 bg-grey-100 rounded-lg p-12 mb-4'>
								<div className='flex-1 flex gap-12 items-center'>
									<PiSealWarningDuotone className='text-xl' />
									<div className='text-12 opacity-75'>Kérjük, hogy a javasolt javításokat piros színnel jelölje.</div>
								</div>
								<Button onClick={() => methods.setValue('comments', questionText || '')} color='primary' variant='contained' disabled={loading || !questionText}>Kérdés másolása</Button>
							</div>
							<Controller control={methods.control} name='comments' render={({ field, fieldState }) => (
								<EditFormHtmlField
									field={field}
									fieldState={fieldState}
									config={{ topic: 'common' }}
									fieldConfig={{
										key: 'comments',
										text: 'Javasolt javítás',
										useRedText: true,
										useBoldText: true,
										storageService: FmStorageTableService,
										browserService: FmBrowserTableService,
										getUrl: item => `${nBossConnection.basePath}fmBrowserTableControl/getFile?key=${item.key}`,
									}} />
							)} />
						</FormProvider>

					</div>}

				</DialogContent>

				{!loading && <DialogActions className='w-full flex items-end justify-between border-t p-20'>
					<Button className={review ? '' : 'w-full'} onClick={() => setReview(!review)} color={review ? 'secondary' : 'primary'} variant='contained'>{review ? 'Vissza' : 'Véleményezés'}</Button>
					{review && <Button onClick={methods.handleSubmit(handleSave)} color='primary' variant='contained'>Vélemény elküldése</Button>}
				</DialogActions>}

			</Dialog>

		</TableCell>
	);
}

export default TableQuestionViewButton;